/**
 * Reads first-touch attribution appended to a link into the app by the landing page's
 * `appUrl()` helper (see `landing-page/src/config.js` and `landing-page/src/attribution.js`),
 * and keeps it in `sessionStorage` for the app session, same-site, first-party, gone when the
 * tab closes.
 *
 * PostHog runs cookieless here too (see initPostHog.js): nothing persists across visits or
 * domains on its own. This small amount of first-party session state is what lets a sign-up be
 * attributed to the campaign that brought the visitor in, with no cookie and no cross-site
 * tracking.
 */
const STORAGE_KEY = 'fg_attribution';
const PARAM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref', 'fg_ref', 'fg_landing',
];

/**
 * Parses attribution off the given query string (defaults to the current URL) and stores it in
 * `sessionStorage`, unless something is already stored for this session, first touch wins.
 * Never mutates the URL/history. Never throws; returns {} on any failure or when there is
 * nothing to capture.
 *
 * @param {string} [search=window.location.search] Injectable for tests.
 * @returns {object} The attribution now held for this session (freshly captured, or whatever
 *   was already there).
 */
export function captureAttribution(search = (typeof window !== 'undefined' ? window.location.search : '')) {
  try {
    const existingRaw = sessionStorage.getItem(STORAGE_KEY);
    if (existingRaw) {
      return JSON.parse(existingRaw);
    }

    const params = new URLSearchParams(search);
    const found = {};
    PARAM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) found[key] = value;
    });

    if (Object.keys(found).length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(found));
    }
    return found;
  } catch {
    return {};
  }
}

/** The attribution captured for this app session, or {} if none was ever captured. */
export function getAttribution() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Maps stored attribution to the person-property keys used with `posthog.identify`'s third
 * ($set_once) argument, so the FIRST value ever recorded for a person sticks even if they sign
 * in again later from a session with no attribution at all.
 */
export function attributionSetOnceProperties() {
  const a = getAttribution();
  const props = {};
  if (a.utm_source) props.utm_source = a.utm_source;
  if (a.utm_medium) props.utm_medium = a.utm_medium;
  if (a.utm_campaign) props.utm_campaign = a.utm_campaign;
  if (a.utm_content) props.utm_content = a.utm_content;
  if (a.utm_term) props.utm_term = a.utm_term;
  if (a.ref) props.ref = a.ref;
  if (a.fg_ref) props.initial_referring_domain = a.fg_ref;
  if (a.fg_landing) props.landing_path = a.fg_landing;
  return props;
}

/** Test-only: clears stored attribution between specs. */
export function _resetForTests() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
